// frontend/client/src/components/DashboardLayout.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate, Link, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../hooks/useTheme'; 
import { useSelectedMedia } from '../context/SelectedMediaContext';
import { api } from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import MinoOS from './MinoOS';
import {
  LayoutDashboard, FlaskConical, Eye, BarChart3, Brain,
  Image, Calendar, Target, Bookmark, User, Settings, CreditCard,
  Crown, Sparkles, Menu, X, ChevronLeft, ChevronRight, Bell, Search, LogOut,
  Film, Plus, Command, Wand2, FileVideo, MessageSquare
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
  { path: '/dashboard/edit-studio', icon: FlaskConical, label: 'Edit Studio' },
  { path: '/dashboard/preview', icon: Eye, label: 'Feed Preview' },
  { path: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/dashboard/media', icon: Image, label: 'Media Library' },
  { path: '/dashboard/calendar', icon: Calendar, label: 'Calendar' },
  { path: '/dashboard/campaigns', icon: Target, label: 'Campaigns' },
  { path: '/dashboard/saved', icon: Bookmark, label: 'Saved' },
];

const BOTTOM_ITEMS = [
  { path: '/dashboard/profile', icon: User, label: 'Profile' },
  { path: '/dashboard/settings', icon: Settings, label: 'Settings' },
  { path: '/dashboard/pricing', icon: CreditCard, label: 'Upgrade' },
];

const CREATION_OPTIONS = [
  { id: 'video',   icon: FileVideo, color: '#4F46E5', label: 'Video Post',   desc: 'Upload, edit, and caption a video',       path: '/dashboard/edit-studio' },
  { id: 'image',   icon: Image,     color: '#8B5CF6', label: 'Image Post',   desc: 'Design or upload an image',               path: '/dashboard/media' },
];

const ALL_COMMANDS = [
  { label: 'Upload Video', path: '/dashboard/edit-studio', icon: FileVideo },
  { label: 'Generate Caption', path: '/dashboard/edit-studio', icon: Wand2 },
  { label: 'View Analytics', path: '/dashboard/analytics', icon: BarChart3 },
  { label: 'Content Calendar', path: '/dashboard/calendar', icon: Calendar },
  { label: 'Brand Settings', path: '/dashboard/settings', icon: Settings },
  { label: 'Feed Preview', path: '/dashboard/preview', icon: Eye },
];

const PLAN_CONFIG = {
  freemium: { label: 'Free', color: '#6B7280', icon: Sparkles },
  creator:  { label: 'Creator', color: '#4F46E5', icon: Crown },
  growth:   { label: 'Growth', color: '#8B5CF6', icon: Crown },
  business: { label: 'Business', color: '#F59E0B', icon: Crown },
  executive:{ label: 'Executive', color: '#10B981', icon: Crown },
};

const DL_CSS = `
  @keyframes wave-move {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .dl-root { display: flex; min-height: 100vh; background: var(--bg-canvas); position: relative; overflow: hidden; }
  
  .dl-sidebar { 
    width: 240px; min-width: 240px; 
    background-image: linear-gradient(rgba(17, 24, 39, 0.85), rgba(17, 24, 39, 0.85)), url('/bento-wave.jpg');
    background-size: 200% 100%;
    background-position: 0% 50%;
    animation: wave-move 30s ease infinite;
    border-right: 1px solid rgba(255,255,255,0.06);
    display: flex; flex-direction: column; transition: width 0.3s ease, min-width 0.3s ease, transform 0.3s ease; position: fixed; top: 0; left: 0; bottom: 0; z-index: 50; overflow-y: auto; overflow-x: hidden; 
  }
  
  .dl-sidebar.collapsed { width: 64px; min-width: 64px; }
  .dl-sidebar.mobile-closed { transform: translateX(-100%); }
  .dl-sidebar.mobile-open { transform: translateX(0); }
  .dl-sidebar-header { display: flex; align-items: center; gap: 10px; padding: 16px 16px 12px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  
  .dl-logo-box { width: 32px; height: 32px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .dl-brand-text { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: white; letter-spacing: -0.02em; white-space: nowrap; }
  .dl-toggle-btn { margin-left: auto; background: rgba(255,255,255,0.06); border: none; color: #9CA3AF; cursor: pointer; width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all 0.15s; flex-shrink: 0; }
  .dl-toggle-btn:hover { background: rgba(255,255,255,0.12); color: white; }
  .dl-nav { flex: 1; padding: 12px 10px; display: flex; flex-direction: column; gap: 2px; overflow-y: auto; }
  .dl-nav-item { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 10px; color: #9CA3AF; text-decoration: none; font-size: 13px; font-weight: 600; font-family: var(--font-body); transition: all 0.15s; white-space: nowrap; position: relative; }
  .dl-nav-item:hover { background: rgba(255,255,255,0.06); color: #E5E7EB; }
  .dl-nav-item.active { background: rgba(79, 70, 229, 0.2); color: #A5B4FC; }
  .dl-nav-item.active::before { content: ''; position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 3px; height: 20px; background: linear-gradient(180deg, #4F46E5, #8B5CF6); border-radius: 0 3px 3px 0; }
  .dl-nav-icon { width: 18px; height: 18px; flex-shrink: 0; }
  .dl-nav-label { white-space: nowrap; overflow: hidden; opacity: 1; transition: opacity 0.2s; }
  .dl-sidebar.collapsed .dl-nav-label { opacity: 0; width: 0; }
  .dl-sidebar.collapsed .dl-brand-text { opacity: 0; width: 0; }
  .dl-bottom { padding: 10px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; gap: 2px; }
  .dl-plan-badge { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 10px; background: rgba(255,255,255,0.04); margin: 0 0 6px; cursor: pointer; text-decoration: none; transition: background 0.15s; }
  .dl-plan-badge:hover { background: rgba(255,255,255,0.08); }
  .dl-plan-icon { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .dl-plan-name { font-size: 12px; font-weight: 700; color: white; margin: 0; }
  .dl-plan-sublabel { font-size: 10px; color: #6B7280; margin: 0; }
  
  .dl-main { flex: 1; margin-left: 240px; display: flex; flex-direction: column; min-height: 100vh; transition: margin-left 0.3s ease; position: relative; z-index: 10; }
  .dl-main.expanded { margin-left: 64px; }
  
  .dl-topbar { height: 64px; background: var(--bg-surface); border-bottom: 1px solid var(--border-light); display: flex; align-items: center; justify-content: space-between; padding: 0 24px; position: sticky; top: 0; z-index: 40; }
  .dl-topbar-left { display: flex; align-items: center; gap: 16px; }
  .dl-menu-btn { display: none; background: none; border: none; color: var(--text-main); cursor: pointer; padding: 4px; }
  
  .dl-search-box { position: relative; cursor: text; }
  .dl-search-input { padding: 8px 12px 8px 36px; border-radius: 10px; border: 1px solid var(--border-light); background: var(--bg-canvas); font-size: 13px; font-family: var(--font-body); color: var(--text-main); outline: none; width: 220px; transition: all 0.2s; }
  .dl-search-input:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1); }
  .dl-cmd-hint { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); font-size: 10px; font-weight: 700; color: var(--text-muted); background: var(--border-light); padding: 2px 6px; border-radius: 4px; pointer-events: none; }

  .dl-topbar-right { display: flex; align-items: center; gap: 16px; }
  .dl-header-create-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 10px; background: linear-gradient(135deg, #4F46E5, #8B5CF6); color: white; font-size: 13px; font-weight: 700; border: none; cursor: pointer; box-shadow: var(--shadow-brand); transition: opacity 0.2s, transform 0.1s; }
  .dl-header-create-btn:hover { opacity: 0.9; transform: translateY(-1px); }
  .dl-icon-btn { width: 38px; height: 38px; border-radius: 10px; border: 1px solid var(--border-light); background: var(--bg-surface); color: var(--text-muted); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; position: relative; }
  .dl-icon-btn:hover { border-color: var(--brand-border); color: var(--brand-primary); }
  .dl-notif-dot { position: absolute; top: 8px; right: 8px; width: 8px; height: 8px; border-radius: 50%; background: #EF4444; border: 2px solid var(--bg-surface); }
  .dl-avatar { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; border: 2px solid var(--border-light); cursor: pointer; }
  
  .dl-content { flex: 1; padding: 0; overflow-y: auto; position: relative; }
  .dl-mobile-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 45; }
  .dl-mobile-overlay.open { display: block; }

  .dl-ambient-glow { position: fixed; top: -10%; left: -10%; width: 50vw; height: 50vw; background: radial-gradient(circle, var(--brand-secondary) 0%, transparent 70%); filter: blur(80px); z-index: 0; pointer-events: none; }
  
  /* Global Asset Dock */
  .dl-asset-dock { position: fixed; bottom: 24px; right: 24px; background: var(--bg-surface); backdrop-filter: blur(24px); border: 1px solid var(--brand-border); border-radius: 16px; padding: 12px; display: flex; align-items: center; gap: 14px; box-shadow: var(--shadow-lg); z-index: 99; transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
  .dl-asset-dock:hover { border-color: var(--brand-primary); }
  .dl-dock-actions { display: flex; gap: 8px; margin-left: auto; padding-left: 16px; border-left: 1px solid var(--border-light); }
  .dl-dock-btn { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 8px; border: 1px solid transparent; background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .dl-dock-btn:hover { background: var(--brand-primary); color: var(--brand-primary-contrast, white); }

  /* Global Modals (Creation + Command Palette) */
  .dl-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; }
  .dl-modal { background: var(--bg-surface); width: 100%; max-width: 480px; border-radius: 20px; padding: 28px; box-shadow: var(--shadow-xl); border: 1px solid var(--border-light); }
  .dl-modal-title { font-family: var(--font-display); font-size: 20px; font-weight: 700; color: var(--text-main); margin: 0 0 6px; }
  .dl-modal-sub { font-size: 13px; color: var(--text-muted); margin: 0 0 24px; }
  .dl-creation-option { display: flex; align-items: center; gap: 16px; padding: 16px; border-radius: 14px; border: 2px solid var(--border-light); background: var(--bg-canvas); cursor: pointer; transition: all 0.2s; width: 100%; text-align: left; margin-bottom: 10px; }
  .dl-creation-option:last-child { margin-bottom: 0; }
  .dl-creation-option:hover { border-color: var(--brand-primary); background: var(--brand-secondary); }
  .dl-creation-option-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .dl-creation-label { font-size: 14px; font-weight: 700; color: var(--text-main); margin: 0 0 2px; }
  .dl-creation-desc { font-size: 12px; color: var(--text-muted); margin: 0; }

  .dl-cmd-input-container { display: flex; align-items: center; padding: 0 24px; border-bottom: 1px solid var(--border-light); }
  .dl-cmd-overlay-input { width: 100%; background: transparent; border: none; padding: 20px 12px; font-size: 18px; color: var(--text-main); outline: none; font-family: var(--font-body); }
  .dl-cmd-overlay-input::placeholder { color: var(--text-muted); }
  .dl-cmd-list { padding: 12px; max-height: 320px; overflow-y: auto; }
  .dl-cmd-item { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 12px; cursor: pointer; color: var(--text-main); font-weight: 600; font-size: 14px; transition: all 0.15s; }
  .dl-cmd-item:hover { background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); }

  .dl-sidebar.collapsed .dl-sidebar-header {
    padding: 16px 0;
    justify-content: center;
  }
  .dl-sidebar.collapsed .dl-logo-box {
    display: none;
  }
  .dl-sidebar.collapsed .dl-toggle-btn {
    margin-left: 0;
  }

  @media (max-width: 1024px) {
    .dl-sidebar { transition: transform 0.3s ease; }
    .dl-main { margin-left: 0 !important; }
    .dl-menu-btn { display: flex; }
    .dl-toggle-btn { display: none; }
    .dl-search-box { display: none; }
    .dl-header-create-btn span { display: none; }
  }
`;

export default function DashboardLayout() {
  const { currentUser, userData, logout } = useAuth();
  const { addToast } = useToast();

  // Feedback — minimal beta mechanism, posts straight to the founder inbox
  // via the already-working EmailService, not a new vendor integration.
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      const res = await api.post('/feedback', {
        message: feedbackMessage.trim(),
        page: location.pathname,
        userEmail: currentUser?.email,
      });
      if (res.success) {
        addToast({ type: 'success', message: 'Thanks — that landed straight in Jennell\'s inbox.' });
        setFeedbackMessage('');
        setShowFeedbackModal(false);
      } else {
        addToast({ type: 'error', message: res.error || 'Failed to send feedback' });
      }
    } catch (err) {
      addToast({ type: 'error', message: 'Failed to send feedback' });
    } finally {
      setFeedbackSending(false);
    }
  };
  const navigate = useNavigate();
  useTheme();
  const { selectedMedia } = useSelectedMedia();

  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications] = useState(3);

  // Safe window width for SSR and responsive behavior
  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1025
  );

  useEffect(() => {
    const handleResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global Modals State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [cmdSearch, setCmdSearch] = useState('');

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 1024) setMobileOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cmd+K Listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setShowCreateModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const executeCommand = (path) => {
    setShowCommandPalette(false);
    setCmdSearch('');
    navigate(path);
  };

  const handleCreationChoice = (path) => {
    setShowCreateModal(false);
    navigate(path);
  };

  const filteredCommands = ALL_COMMANDS.filter(cmd => 
    cmd.label.toLowerCase().includes(cmdSearch.toLowerCase())
  );

  const plan = PLAN_CONFIG[userData?.subscriptionPlan] || PLAN_CONFIG.freemium;
  const brandName = userData?.brandName || 'Infleuince';
  const avatarUrl = userData?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(brandName)}&background=4F46E5&color=fff`;

  const sidebarClasses = [
    'dl-sidebar',
    collapsed && winWidth > 1024 ? 'collapsed' : '',
    winWidth <= 1024 ? (mobileOpen ? 'mobile-open' : 'mobile-closed') : '',
  ].filter(Boolean).join(' ');

  const pageVariants = {
    initial: { opacity: 0, y: 15, scale: 0.99 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <div className="dl-root">
      <style>{DL_CSS}</style>
      <div className="dl-ambient-glow" />

      {winWidth <= 1024 && (
        <div className={`dl-mobile-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={sidebarClasses}>
        <div className="dl-sidebar-header">
          <div className="dl-logo-box">
             <img src="/favicon.png" alt="Infleuince" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <span className="dl-brand-text">Infleuince</span>
          <button 
            className="dl-toggle-btn" 
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <nav className="dl-nav">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`dl-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => winWidth <= 1024 && setMobileOpen(false)}
            >
              <item.icon size={18} className="dl-nav-icon" />
              <span className="dl-nav-label">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="dl-bottom">
          <Link to="/dashboard/pricing" className="dl-plan-badge">
            <div className="dl-plan-icon" style={{ background: `${plan.color}20` }}>
              <plan.icon size={14} color={plan.color} />
            </div>
            <div style={{ overflow: 'hidden' }}>
              <p className="dl-plan-name">{plan.label}</p>
              <p className="dl-plan-sublabel">{userData?.subscriptionPlan === 'freemium' ? 'Upgrade for more' : 'Active plan'}</p>
            </div>
          </Link>

          {BOTTOM_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`dl-nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              <item.icon size={18} className="dl-nav-icon" />
              <span className="dl-nav-label">{item.label}</span>
            </Link>
          ))}

          <button
            className="dl-nav-item"
            onClick={logout}
            style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
            aria-label="Logout"
          >
            <LogOut size={18} className="dl-nav-icon" />
            <span className="dl-nav-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`dl-main ${collapsed ? 'expanded' : ''}`}>
        
        {/* Topbar */}
        <header className="dl-topbar">
          <div className="dl-topbar-left">
            <button 
              className="dl-menu-btn" 
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Open menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            
            {/* Search triggers Command Palette */}
            <div className="dl-search-box" onClick={() => setShowCommandPalette(true)}>
              <Search size={14} color="var(--text-light)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input 
                type="text" 
                placeholder="Search or jump to..." 
                className="dl-search-input" 
                readOnly 
                aria-label="Search or jump to"
              />
              <span className="dl-cmd-hint">⌘K</span>
            </div>
          </div>
          
          <div className="dl-topbar-right">
            <button className="dl-header-create-btn" onClick={() => setShowCreateModal(true)}>
              <Plus size={16} /> <span>Create</span>
            </button>
            <button className="dl-icon-btn" aria-label="Send feedback" onClick={() => setShowFeedbackModal(true)}>
              <MessageSquare size={18} />
            </button>
            <button className="dl-icon-btn" aria-label="Notifications">
              <Bell size={18} />
              {notifications > 0 && <span className="dl-notif-dot" />}
            </button>
            <Link to="/dashboard/profile">
              <img src={avatarUrl} alt={brandName} className="dl-avatar" />
            </Link>
          </div>
        </header>

        {/* Dynamic Outlet */}
        <div className="dl-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              style={{ minHeight: '100%' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <MinoOS />

      {/* Enhanced Global Asset Dock */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 100 }}
            className="dl-asset-dock"
          >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: '#111', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
               {selectedMedia.type === 'video' ? <Film size={20} color="var(--text-light)"/> : <Image size={20} color="var(--text-light)"/>}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>Active Context</p>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedMedia.name || 'Untitled Asset'}</p>
            </div>
            <div className="dl-dock-actions">
              <button className="dl-dock-btn" onClick={(e) => { e.stopPropagation(); navigate('/dashboard/edit-studio', { state: { video: selectedMedia } }); }}>
                <Wand2 size={14} /> Edit &amp; Caption
              </button>
              <button className="dl-dock-btn" onClick={(e) => { e.stopPropagation(); navigate('/dashboard/preview', { state: { video: selectedMedia } }); }}>
                <Eye size={14} /> Preview
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global Creation Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="dl-modal-overlay" onClick={() => setShowCreateModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="dl-modal" onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 className="dl-modal-title">What are you creating?</h2>
                  <p className="dl-modal-sub">Choose a format and we'll take you to the right workflow.</p>
                </div>
                <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} aria-label="Close creation modal">
                  <X size={20} />
                </button>
              </div>
              {CREATION_OPTIONS.map(opt => (
                <button key={opt.id} className="dl-creation-option" onClick={() => handleCreationChoice(opt.path)}>
                  <div className="dl-creation-option-icon" style={{ background: `${opt.color}15` }}>
                    <opt.icon size={22} color={opt.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="dl-creation-label">{opt.label}</p>
                    <p className="dl-creation-desc">{opt.desc}</p>
                  </div>
                  <ChevronRight size={16} color="var(--text-light)" />
                </button>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global Command Palette */}
      <AnimatePresence>
        {showCommandPalette && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="dl-modal-overlay" onClick={() => setShowCommandPalette(false)} style={{ alignItems: 'flex-start', paddingTop: '12vh' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -20 }}
              className="dl-modal"
              style={{ padding: 0, overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="dl-cmd-input-container">
                <Command size={20} color="var(--text-muted)" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Type a command or search..."
                  className="dl-cmd-overlay-input"
                  value={cmdSearch}
                  onChange={e => setCmdSearch(e.target.value)}
                  aria-label="Command palette search"
                />
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', background: 'var(--bg-canvas)', padding: '4px 8px', borderRadius: 6 }}>ESC</div>
              </div>
              <div className="dl-cmd-list">
                {filteredCommands.length > 0 ? filteredCommands.map((cmd, idx) => (
                  <div key={idx} className="dl-cmd-item" onClick={() => executeCommand(cmd.path)}>
                    <cmd.icon size={16} />
                    {cmd.label}
                  </div>
                )) : (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No commands found.</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedbackModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="dl-modal-overlay" onClick={() => setShowFeedbackModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="dl-modal" onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 className="dl-modal-title">Got feedback?</h2>
                  <p className="dl-modal-sub">Bug, confusing moment, or something you wish worked differently — this goes straight to Jennell.</p>
                </div>
                <button onClick={() => setShowFeedbackModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} aria-label="Close feedback modal">
                  <X size={20} />
                </button>
              </div>
              <textarea
                autoFocus
                value={feedbackMessage}
                onChange={e => setFeedbackMessage(e.target.value)}
                placeholder="What happened, or what would you change?"
                rows={5}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 10,
                  border: '1px solid var(--border-light)', fontSize: 14,
                  fontFamily: 'var(--font-body)', outline: 'none', resize: 'vertical',
                  boxSizing: 'border-box', marginBottom: 16
                }}
              />
              <button
                className="dl-header-create-btn"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={handleSubmitFeedback}
                disabled={feedbackSending || !feedbackMessage.trim()}
              >
                {feedbackSending ? 'Sending…' : 'Send Feedback'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}